import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { ArrowRight, Eye, EyeOff } from "lucide-react";
import { SiteFooter } from "../components/SiteFooter";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_da98dbcd-f751-4482-bd4e-a7e20426ba71/artifacts/tm6wbpvj_CD8D187A-3FEA-430F-A897-685C7F8A2178.png";

function parseErrorDetail(err) {
  const detail = err.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e.msg).join(" ");
  return "Something went wrong. Please try again.";
}

function LoginForm({ onSubmit, loading, error }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);

  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit({ email, password }); }} className="space-y-4" data-testid="portal-auth-form">
      <div>
        <label className="block text-[10px] uppercase tracking-wider mb-1.5" style={{ color: "var(--text-tertiary)" }}>Email</label>
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="portal-email-input"
          className="w-full px-4 py-3 text-sm outline-none"
          style={{ border: "1px solid var(--border)", background: "var(--bg-secondary)", color: "var(--text-primary)" }}
          placeholder="your@email.com" />
      </div>
      <div>
        <label className="block text-[10px] uppercase tracking-wider mb-1.5" style={{ color: "var(--text-tertiary)" }}>Password</label>
        <div className="relative">
          <input type={showPw ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="portal-password-input"
            className="w-full px-4 py-3 text-sm outline-none pr-12"
            style={{ border: "1px solid var(--border)", background: "var(--bg-secondary)", color: "var(--text-primary)" }}
            placeholder="Your password" />
          <button type="button" onClick={() => setShowPw(!showPw)}
            className="absolute right-3 top-1/2 -translate-y-1/2 opacity-50 hover:opacity-100"
            style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-tertiary)" }}>
            {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>
      </div>
      {error && <p className="text-xs py-2 px-3" style={{ color: "#DC2626", background: "rgba(220,38,38,0.05)", border: "1px solid rgba(220,38,38,0.15)" }} data-testid="portal-auth-error">{error}</p>}
      <Button type="submit" disabled={loading} data-testid="portal-auth-submit" className="w-full py-3 text-sm font-semibold" style={{ background: "var(--accent)", color: "#FFFFFF" }}>
        {loading ? "Please wait..." : <>Sign In <ArrowRight size={14} className="ml-2" /></>}
      </Button>
    </form>
  );
}

function RegisterForm({ onSubmit, loading, error }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);

  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit({ name, email, password }); }} className="space-y-4" data-testid="portal-auth-form">
      <div>
        <label className="block text-[10px] uppercase tracking-wider mb-1.5" style={{ color: "var(--text-tertiary)" }}>Full Name</label>
        <input type="text" value={name} onChange={(e) => setName(e.target.value)} required data-testid="portal-name-input"
          className="w-full px-4 py-3 text-sm outline-none"
          style={{ border: "1px solid var(--border)", background: "var(--bg-secondary)", color: "var(--text-primary)" }}
          placeholder="Your name" />
      </div>
      <div>
        <label className="block text-[10px] uppercase tracking-wider mb-1.5" style={{ color: "var(--text-tertiary)" }}>Email</label>
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="portal-email-input"
          className="w-full px-4 py-3 text-sm outline-none"
          style={{ border: "1px solid var(--border)", background: "var(--bg-secondary)", color: "var(--text-primary)" }}
          placeholder="your@email.com" />
      </div>
      <div>
        <label className="block text-[10px] uppercase tracking-wider mb-1.5" style={{ color: "var(--text-tertiary)" }}>Password</label>
        <div className="relative">
          <input type={showPw ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} data-testid="portal-password-input"
            className="w-full px-4 py-3 text-sm outline-none pr-12"
            style={{ border: "1px solid var(--border)", background: "var(--bg-secondary)", color: "var(--text-primary)" }}
            placeholder="Min 6 characters" />
          <button type="button" onClick={() => setShowPw(!showPw)}
            className="absolute right-3 top-1/2 -translate-y-1/2 opacity-50 hover:opacity-100"
            style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-tertiary)" }}>
            {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>
      </div>
      {error && <p className="text-xs py-2 px-3" style={{ color: "#DC2626", background: "rgba(220,38,38,0.05)", border: "1px solid rgba(220,38,38,0.15)" }} data-testid="portal-auth-error">{error}</p>}
      <Button type="submit" disabled={loading} data-testid="portal-auth-submit" className="w-full py-3 text-sm font-semibold" style={{ background: "var(--accent)", color: "#FFFFFF" }}>
        {loading ? "Please wait..." : <>Create Account <ArrowRight size={14} className="ml-2" /></>}
      </Button>
    </form>
  );
}

export default function PortalAuth() {
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (payload) => {
    setError("");
    setLoading(true);
    try {
      const endpoint = mode === "login" ? "login" : "register";
      await axios.post(`${API}/client/${endpoint}`, payload, { withCredentials: true });
      toast.success(mode === "login" ? "Welcome back!" : "Account created!");
      navigate("/portal");
    } catch (err) {
      setError(parseErrorDetail(err));
    } finally {
      setLoading(false);
    }
  };

  const title = mode === "login" ? "Client Portal" : "Create Account";
  const subtitle = mode === "login"
    ? "Sign in to view your submissions and bookings."
    : "Register to track your submissions and book sessions.";
  const toggleText = mode === "login"
    ? "Don't have an account? Register"
    : "Already have an account? Sign in";

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6" style={{ background: "var(--bg-primary)" }} data-testid="portal-auth-page">
      <div className="w-full max-w-sm">
        <div className="text-center mb-10">
          <a href="/"><img src={LOGO_URL} alt="Green Routes" className="h-10 mx-auto mb-8 logo-blend" /></a>
          <h1 className="text-2xl sm:text-3xl tracking-tight mb-2" style={{ color: "var(--text-primary)" }}>{title}</h1>
          <p className="text-sm" style={{ color: "var(--text-secondary)" }}>{subtitle}</p>
        </div>

        {mode === "login"
          ? <LoginForm onSubmit={handleSubmit} loading={loading} error={error} />
          : <RegisterForm onSubmit={handleSubmit} loading={loading} error={error} />
        }

        <div className="text-center mt-6">
          <button type="button" onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}
            className="text-sm" data-testid="portal-toggle-mode"
            style={{ color: "var(--accent)", background: "none", border: "none", cursor: "pointer", textDecoration: "underline" }}>
            {toggleText}
          </button>
        </div>

        <div className="mt-8">
          <SiteFooter />
        </div>
      </div>
    </div>
  );
}
